import { create } from "zustand"
import { vanillaClient } from "@/lib/trpc"

interface LookupItem {
  code: string
  name: string
  nameEn?: string
  sortOrder?: number
}

interface GradeItem extends LookupItem {
  stageCode: string
}

interface SubjectItem extends LookupItem {
  category?: string
  type?: string
  icon?: string
  parentCode?: string
}

interface RegionItem extends LookupItem {
  parentCode?: string | null
  level: number
  fullName?: string
}

interface CompetitionItem extends LookupItem {
  subjectCode?: string
  levelCode?: string
  description?: string
  website?: string
}

interface DifficultyLevelItem {
  code: number
  name: string
  sortOrder?: number
}

interface LookupState {
  // Data
  stages: LookupItem[]
  grades: GradeItem[]
  subjects: SubjectItem[]
  regions: RegionItem[]
  roles: LookupItem[]
  examTypes: LookupItem[]
  examCategories: LookupItem[]
  questionTypes: LookupItem[]
  mistakeStatuses: LookupItem[]
  errorReasons: LookupItem[]
  competitions: CompetitionItem[]
  competitionLevels: LookupItem[]
  competitionRounds: LookupItem[]
  difficultyLevels: DifficultyLevelItem[]

  // Loading states
  loading: boolean
  error: string | null

  // Actions
  fetchAll: () => Promise<void>
  fetchGradesByStage: (stageCode: string) => Promise<void>
  fetchSubjectsByStage: (stageCode: string) => Promise<void>
  fetchRegionsByParent: (parentCode: string) => Promise<void>
  fetchCompetitionsBySubject: (subjectCode: string) => Promise<void>
}

export const useLookupStore = create<LookupState>((set, get) => ({
  stages: [],
  grades: [],
  subjects: [],
  regions: [],
  roles: [],
  examTypes: [],
  examCategories: [],
  questionTypes: [],
  mistakeStatuses: [],
  errorReasons: [],
  competitions: [],
  competitionLevels: [],
  competitionRounds: [],
  difficultyLevels: [],

  loading: false,
  error: null,

  fetchAll: async () => {
    set({ loading: true, error: null })
    try {
      // Fetch all lookup data in parallel (不获取 subjects，subjects 按学段单独加载)
      const [
        stages,
        regions,
        roles,
        examTypes,
        examCategories,
        questionTypes,
        mistakeStatuses,
        errorReasons,
        competitionLevels,
        competitionRounds,
        difficultyLevels,
      ] = await Promise.all([
        vanillaClient.lookup.stages.query(),
        vanillaClient.lookup.regions.query({ level: 0 }),
        vanillaClient.lookup.roles.query(),
        vanillaClient.lookup.examTypes.query(),
        vanillaClient.lookup.examCategories.query(),
        vanillaClient.lookup.questionTypes.query(),
        vanillaClient.lookup.mistakeStatuses.query(),
        vanillaClient.lookup.errorReasons.query(),
        vanillaClient.lookup.competitionLevels.query(),
        vanillaClient.lookup.competitionRounds.query(),
        vanillaClient.lookup.difficultyLevels.query(),
      ])

      set({
        stages,
        regions,
        roles,
        examTypes,
        examCategories,
        questionTypes,
        mistakeStatuses,
        errorReasons,
        competitionLevels,
        competitionRounds,
        difficultyLevels,
        loading: false,
      })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Failed to fetch lookup data",
        loading: false,
      })
    }
  },

  fetchGradesByStage: async (stageCode: string) => {
    try {
      const grades = await vanillaClient.lookup.grades.query({ stageCode })
      set({ grades })
    } catch (err) {
      console.error("Failed to fetch grades:", err)
    }
  },

  fetchSubjectsByStage: async (stageCode: string) => {
    try {
      const { subjects: currentSubjects } = get()
      // 如果已经加载过该学段的 subjects，且数量相同，则跳过
      if (currentSubjects.length > 0 && currentSubjects[0]?.stageCode === stageCode) {
        return
      }
      const stageSubjects = await vanillaClient.lookup.subjects.query({ stageCode })
      set({ subjects: stageSubjects.map(s => ({ ...s, stageCode })) })
    } catch (err) {
      console.error("Failed to fetch subjects:", err)
    }
  },

  fetchRegionsByParent: async (parentCode: string) => {
    try {
      const regions = await vanillaClient.lookup.regions.query({ parentCode })
      // Merge with existing regions
      set((state) => ({
        regions: [...state.regions.filter(r => r.parentCode !== parentCode), ...regions],
      }))
    } catch (err) {
      console.error("Failed to fetch regions:", err)
    }
  },

  fetchCompetitionsBySubject: async (subjectCode: string) => {
    try {
      const competitions = await vanillaClient.lookup.competitions.query({ subjectCode })
      set({ competitions })
    } catch (err) {
      console.error("Failed to fetch competitions:", err)
    }
  },
}))
